import type { TracifyContext } from "../ai/request-context.ts";
import type { Granularity } from "../kpi-api-types.ts";
import {
	type DateScopeOverrideInput,
	type ResolvedComparisonRange,
	resolveDateScope,
} from "./date-scope.ts";

export type AnalyticsCustomerType = "new" | "returning" | "total";

export type AnalyticsScopeInput = {
	presetId: string;
	storeIds: string[];
	startDate: string;
	endDate: string;
	comparisonPreset?: string | null;
	comparisonStartDate?: string | null;
	comparisonEndDate?: string | null;
	customerType?: AnalyticsCustomerType | null;
	filters?: string | null;
};

export type AnalyticsScopeOverrides = {
	channels?: string[] | null;
	topN?: number | null;
	rankBy?: string | null;
	granularity?: Granularity | null;
};

export type AnalyticsScope = {
	presetId: string;
	storeIds: string[];
	startDate: string;
	endDate: string;
	comparison: ResolvedComparisonRange | null;
	customerType?: AnalyticsCustomerType | null;
	filters?: string | null;
	channels?: string[] | null;
	topN?: number | null;
	rankBy?: string | null;
	granularity?: Granularity | null;
};

export type CustomerJourneyScopeInput = {
	storeIds: string[];
	startDate: string;
	endDate: string;
	customerType?: AnalyticsCustomerType | null;
	filters?: string | Record<string, unknown> | unknown[] | null;
};

export type CustomerJourneyScope = {
	storeIds: string[];
	startDate: string;
	endDate: string;
	customerType?: AnalyticsCustomerType | null;
	filters?: string | Record<string, unknown> | unknown[] | null;
};

function hasExplicitComparisonDates(input: {
	comparisonStartDate?: string | null;
	comparisonEndDate?: string | null;
}) {
	return Boolean(input.comparisonStartDate && input.comparisonEndDate);
}

function toDateScopeOverrideInput(
	input: Pick<
		AnalyticsScopeInput,
		"comparisonPreset" | "comparisonStartDate" | "comparisonEndDate"
	>,
): DateScopeOverrideInput {
	const isNoComparisonPreset = input.comparisonPreset === "no_comparison";

	return {
		comparisonStartDate: isNoComparisonPreset
			? null
			: input.comparisonStartDate,
		comparisonEndDate: isNoComparisonPreset ? null : input.comparisonEndDate,
		comparePreviousPeriod:
			input.comparisonPreset === "last_period" &&
			!hasExplicitComparisonDates(input)
				? true
				: undefined,
	};
}

export function createAnalyticsScope(
	input: AnalyticsScopeInput,
	overrides?: AnalyticsScopeOverrides,
): AnalyticsScope {
	const isNoComparisonPreset = input.comparisonPreset === "no_comparison";
	const compared = isNoComparisonPreset
		? false
		: Boolean(input.comparisonPreset) || hasExplicitComparisonDates(input);
	const dateScope = resolveDateScope(
		{
			selectedStartDate: input.startDate,
			selectedEndDate: input.endDate,
			compared,
			comparisonStartDate: isNoComparisonPreset
				? null
				: input.comparisonStartDate,
			comparisonEndDate: isNoComparisonPreset
				? null
				: input.comparisonEndDate,
		},
		toDateScopeOverrideInput(input),
	);

	return {
		presetId: input.presetId,
		storeIds: input.storeIds,
		startDate: dateScope.startDate,
		endDate: dateScope.endDate,
		comparison: dateScope.comparison,
		customerType: input.customerType,
		filters: input.filters,
		channels: overrides?.channels,
		topN: overrides?.topN,
		rankBy: overrides?.rankBy,
		granularity: overrides?.granularity,
	};
}

export function createAnalyticsScopeFromTracifyContext(
	context: TracifyContext,
	input: DateScopeOverrideInput = {},
	overrides?: AnalyticsScopeOverrides,
): AnalyticsScope {
	const dateScope = resolveDateScope(context, input);

	return {
		presetId: context.selectedPresetId,
		storeIds: context.selectedStoreIds,
		startDate: dateScope.startDate,
		endDate: dateScope.endDate,
		comparison: dateScope.comparison,
		customerType: context.newVsReturning,
		filters: context.filters,
		channels: overrides?.channels ?? context.channels,
		topN: overrides?.topN ?? context.topN,
		rankBy: overrides?.rankBy ?? context.rankBy,
		granularity: overrides?.granularity ?? context.granularity,
	};
}

export function createCustomerJourneyScope(
	input: CustomerJourneyScopeInput,
): CustomerJourneyScope {
	const dateScope = resolveDateScope(
		{
			selectedStartDate: input.startDate,
			selectedEndDate: input.endDate,
			compared: false,
		},
		{ comparePreviousPeriod: false },
	);

	return {
		storeIds: input.storeIds,
		startDate: dateScope.startDate,
		endDate: dateScope.endDate,
		customerType: input.customerType,
		filters: input.filters,
	};
}

export function createCustomerJourneyScopeFromTracifyContext(
	context: TracifyContext,
	input: DateScopeOverrideInput = {},
): CustomerJourneyScope {
	const dateScope = resolveDateScope(context, {
		startDate: input.startDate,
		endDate: input.endDate,
		comparePreviousPeriod: false,
	});

	return {
		storeIds: context.selectedStoreIds,
		startDate: dateScope.startDate,
		endDate: dateScope.endDate,
		customerType: context.newVsReturning,
		filters: context.filters,
	};
}

export function analyticsScopeToTracifyContext(
	scope: AnalyticsScope,
): TracifyContext {
	return {
		selectedPresetId: scope.presetId,
		selectedStoreIds: scope.storeIds,
		selectedStartDate: scope.startDate,
		selectedEndDate: scope.endDate,
		compared: Boolean(scope.comparison),
		comparisonPreset: null,
		comparisonStartDate: scope.comparison?.start ?? null,
		comparisonEndDate: scope.comparison?.end ?? null,
		newVsReturning: scope.customerType,
		filters: scope.filters,
		channels: scope.channels,
		topN: scope.topN,
		rankBy: scope.rankBy,
		granularity: scope.granularity,
	};
}

export function isAnalyticsScope(value: unknown): value is AnalyticsScope {
	return Boolean(
		value &&
			typeof value === "object" &&
			"presetId" in value &&
			"storeIds" in value &&
			"startDate" in value &&
			"endDate" in value,
	);
}

export function isCustomerJourneyScope(
	value: unknown,
): value is CustomerJourneyScope {
	return Boolean(
		value &&
			typeof value === "object" &&
			!("presetId" in value) &&
			"storeIds" in value &&
			"startDate" in value &&
			"endDate" in value,
	);
}
